<template>
	<TransitionRoot :show="isOpen" appear as="template">
		<Dialog as="div" class="relative z-50" @close="handleClose">
			<TransitionChild
				as="template"
				enter="ease-out duration-300"
				enter-from="opacity-0"
				enter-to="opacity-100"
				leave="ease-in duration-200"
				leave-from="opacity-100"
				leave-to="opacity-0"
			>
				<div
					class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
				/>
			</TransitionChild>

			<div class="fixed inset-0 z-10 overflow-y-auto">
				<div
					class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
				>
					<TransitionChild
						as="template"
						enter="ease-out duration-300"
						enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enter-to="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leave-from="opacity-100 translate-y-0 sm:scale-100"
						leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<DialogPanel
							class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
						>
							<div class="bg-white px-4 pb-4 pt-5 sm:p-6">
								<div class="flex items-start justify-between mb-4">
									<DialogTitle
										as="h3"
										class="text-lg font-semibold leading-6 text-gray-900"
									>
										{{ product.Name }}
									</DialogTitle>
									<button
										type="button"
										class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
										@click="handleClose"
									>
										<XMarkIcon class="h-6 w-6" aria-hidden="true" />
									</button>
								</div>

								<!-- Color Variants -->
								<div v-if="colorVariants.length > 0" class="mb-6">
									<label class="block text-sm font-medium text-gray-700 mb-2">
										{{ $t("Color") || "Color" }}
									</label>
									<div class="flex flex-wrap items-center gap-2">
										<button
											v-for="variant in colorVariants"
											:key="variant.Id"
											type="button"
											:class="[
												'relative flex items-center justify-center rounded-full p-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-gray-500',
												selectedColorValueId ===
												getColorValueId(variant, colorParametricId)
													? 'ring-2 ring-gray-900 ring-offset-2'
													: ''
											]"
											:disabled="!isInStock(variant)"
											@click="
												selectColor(getColorValueId(variant, colorParametricId))
											"
										>
											<span
												aria-hidden="true"
												:class="[
													'h-8 w-8 rounded-full border border-gray-darker',
													!isInStock(variant) ? 'opacity-50' : ''
												]"
												:style="{
													backgroundColor: getColorValue(
														variant,
														colorParametricId,
														allParametrics || []
													)
												}"
											/>
											<div
												v-if="!isInStock(variant)"
												class="w-[88%] h-0.5 bg-black absolute -rotate-45"
											></div>
										</button>
									</div>
								</div>

								<!-- Size/Other Variants -->
								<div v-if="otherVariants.length > 0" class="mb-6">
									<label class="block text-sm font-medium text-gray-700 mb-2">
										{{ $t("Size") || "Size" }}
									</label>
									<div class="flex flex-wrap gap-2">
										<button
											v-for="variant in otherVariants"
											:key="variant.Id"
											type="button"
											:class="[
												'bg-white text-gray-900 cursor-pointer group relative flex items-center justify-center py-2 px-3 text-xs font-medium uppercase hover:bg-gray focus:outline-none border',
												selectedSizeValueId ===
												getSizeValueId(variant, sizeParametricId)
													? 'border-gray-900 ring-2 ring-gray-900'
													: 'border-gray-dark',
												!isInStock(variant) ? 'opacity-50' : ''
											]"
											:disabled="!isInStock(variant)"
											@click="
												selectSize(getSizeValueId(variant, sizeParametricId))
											"
										>
											<span class="text-gray-700 z-10">
												{{
													getVariantLabel(
														variant,
														colorParametricId,
														allParametrics
													)
												}}
											</span>
											<span
												v-if="!isInStock(variant)"
												aria-hidden="true"
												class="pointer-events-none absolute -inset-px border-2 border-gray-dark bg-gray-light"
											>
												<svg
													class="absolute inset-0 h-full w-full stroke-2 text-gray-dark"
													viewBox="0 0 100 100"
													preserveAspectRatio="none"
													stroke="currentColor"
												>
													<line
														x1="0"
														y1="100"
														x2="100"
														y2="0"
														vector-effect="non-scaling-stroke"
													/>
												</svg>
											</span>
										</button>
									</div>
								</div>

								<!-- Error Message -->
								<div v-if="errorMessage" class="mb-4 text-sm text-red-600">
									{{ errorMessage }}
								</div>

								<!-- Add to Cart Button -->
								<div class="flex justify-end gap-3">
									<button
										type="button"
										class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
										@click="handleClose"
									>
										{{ $t("Cancel") || "Cancel" }}
									</button>
									<button
										type="button"
										:class="[
											'rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition ease-in-out duration-200',
											canAddToCart
												? 'bg-main-button hover:bg-gray-900 focus-visible:outline-gray-900'
												: 'bg-gray-300 cursor-not-allowed'
										]"
										:disabled="!canAddToCart || cartStore.updating"
										@click="handleAddToCart"
									>
										<div
											v-if="cartStore.updating"
											class="flex items-center gap-2"
										>
											<div
												class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent"
												role="status"
											></div>
											<span>{{ $t("Adding") || "Adding" }}...</span>
										</div>
										<span v-else>{{ $t("Add to Cart") || "Add to Cart" }}</span>
									</button>
								</div>
							</div>
						</DialogPanel>
					</TransitionChild>
				</div>
			</div>
		</Dialog>
	</TransitionRoot>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
	Dialog,
	DialogPanel,
	DialogTitle,
	TransitionRoot,
	TransitionChild
} from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ParametricListInfo,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { useGtmEvents } from "~/composables/useGtmEvents";
import { storeToRefs } from "pinia";
import {
	findColorParametricId,
	getColorValue,
	getVariantLabel,
	isColorVariant,
	isInStock,
	getUniqueColorVariants,
	getUniqueOtherVariants,
	getFirstNonColorParametricId,
	findMatchingVariant,
	getColorValueId,
	getSizeValueId
} from "~/utils/product/variantHelpers";

interface Props {
	product: FilteredProductItem;
	variants: FilteredProductItem[];
	allParametrics: ParametricListValue[];
	isOpen: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	close: [];
}>();

const cartStore = useCartStore();
const productStore = useProductStore();
const { addToCart } = useGtmEvents();
const { getParametricsInformation } = storeToRefs(productStore);

const parametricsInfo = computed<ParametricListInfo[]>(() => {
	return getParametricsInformation.value || [];
});

const colorParametricId = computed<number | null>(() => {
	return findColorParametricId(parametricsInfo.value);
});

const sizeParametricId = computed<number | null>(() => {
	if (!props.variants || props.variants.length === 0) {
		return null;
	}
	const firstVariant = props.variants[0];
	return getFirstNonColorParametricId(firstVariant, colorParametricId.value);
});

const colorVariants = computed(() => {
	if (
		!colorParametricId.value ||
		!props.allParametrics ||
		props.allParametrics.length === 0
	)
		return [];

	const colorVariantsList = props.variants.filter((v) =>
		isColorVariant(v, colorParametricId.value)
	);
	return getUniqueColorVariants(colorVariantsList, colorParametricId.value);
});

const otherVariants = computed(() => {
	if (
		!sizeParametricId.value ||
		!props.variants ||
		props.variants.length === 0
	) {
		return [];
	}

	let variantsToShow = props.variants;

	if (colorParametricId.value && selectedColorValueId.value !== null) {
		variantsToShow = variantsToShow.filter((v) => {
			const colorValueId = getColorValueId(v, colorParametricId.value);
			return colorValueId === selectedColorValueId.value;
		});
	} else if (colorParametricId.value) {
		variantsToShow = variantsToShow.filter(
			(v) => !isColorVariant(v, colorParametricId.value)
		);
	}

	return getUniqueOtherVariants(variantsToShow, colorParametricId.value);
});

const selectedColorValueId = ref<number | null>(null);
const selectedSizeValueId = ref<number | null>(null);
const errorMessage = ref<string>("");

const canAddToCart = computed(() => {
	if (colorVariants.value.length > 0 && selectedColorValueId.value === null) {
		return false;
	}
	if (otherVariants.value.length > 0 && selectedSizeValueId.value === null) {
		return false;
	}

	const selectedVariant = findMatchingVariant(
		props.variants,
		colorParametricId.value,
		selectedColorValueId.value,
		sizeParametricId.value,
		selectedSizeValueId.value
	);

	if (!selectedVariant) {
		return false;
	}

	return selectedVariant.IsBuyable !== false;
});

const selectColor = (valueId: number | null) => {
	selectedColorValueId.value = valueId;
	if (otherVariants.value.length > 0) {
		selectedSizeValueId.value = null;
	}
	errorMessage.value = "";
};

const selectSize = (valueId: number | null) => {
	selectedSizeValueId.value = valueId;
	errorMessage.value = "";
};

const handleClose = () => {
	selectedColorValueId.value = null;
	selectedSizeValueId.value = null;
	errorMessage.value = "";
	emit("close");
};

const handleAddToCart = async () => {
	if (!canAddToCart.value) {
		return;
	}

	const selectedVariant = findMatchingVariant(
		props.variants,
		colorParametricId.value,
		selectedColorValueId.value,
		sizeParametricId.value,
		selectedSizeValueId.value
	);

	if (!selectedVariant) {
		errorMessage.value = "Variant not found";
		return;
	}

	try {
		await cartStore.addToCart({
			PartNo: selectedVariant.PartNo!,
			Quantity: 1,
			PriceListId:
				selectedVariant.PriceListId || productStore?.priceLists?.[0]?.Id,
			Comment: null,
			Name: selectedVariant.VariantName || selectedVariant.Name
		});

		addToCart(selectedVariant);
		handleClose();
	} catch (error) {
		errorMessage.value = "Failed to add item to cart";
		console.error("Error adding to cart:", error);
	}
};

watch(
	() => props.isOpen,
	(newValue) => {
		if (newValue) {
			selectedColorValueId.value = null;
			selectedSizeValueId.value = null;
			errorMessage.value = "";
		}
	}
);
</script>
